import { Card } from "react-bootstrap";
import type {
  AccountRecord,
  HouseholdModelInfo,
  ModelAssignmentData,
  SoftCompletionData,
} from "../lib/explainTypes";
import { useAccountResolver } from "../hooks/useAccountResolver";
import CompletionStrategyPanel from "./CompletionStrategyPanel";
import FallbackAssignmentView from "./assignment/FallbackAssignmentView";
import PreferenceAssignmentView from "./assignment/PreferenceAssignmentView";
import SoftCompletionView from "./soft/SoftCompletionView";

interface Props {
  modelAssignment: ModelAssignmentData;
  accounts: AccountRecord[];
  // Household model from `request.household_model` — the soft-completion
  // sections compare account models against it (APP-1362).
  householdModel?: HouseholdModelInfo | null;
}

// Reads `response.model_assignment` + `response.accounts`.
//
// APP-1238 structural fix: when strategy is "fallback",
// `model_assignment.completion === null` and no completion view is
// mounted. The dataclass contract enforces this; this component
// mirrors the same contract on the client.
export default function ModelAssignmentPanel({
  modelAssignment,
  accounts,
  householdModel = null,
}: Props) {
  // Blocking positions in the overshoot payload carry only `account_id`
  // (the v3 payload is PII-free); resolve display names from the loaded
  // household snapshot.
  const resolveAccount = useAccountResolver();
  const resolveAccountName = (accountId: number): string => {
    const account = accounts.find((a) => a.account_id === accountId);
    return account ? resolveAccount(account).name : `Account ${accountId}`;
  };

  return (
    <Card className="mb-3">
      <Card.Header>Model Assignment — {modelAssignment.strategy}</Card.Header>
      <Card.Body>
        {modelAssignment.strategy === "fallback" && (
          <FallbackAssignmentView accounts={accounts} />
        )}
        {modelAssignment.strategy === "preference" && (
          <PreferenceAssignmentView modelAssignment={modelAssignment} />
        )}
        {modelAssignment.strategy === "completion" &&
          modelAssignment.completion && (
            <CompletionStrategyPanel
              data={modelAssignment.completion}
              resolveAccountName={resolveAccountName}
            />
          )}
        {/* soft_completion renders the mockup-approved section sequence
            (APP-1362) — including the extended overshoot panel — in
            place of the generic completion view. */}
        {modelAssignment.strategy === "soft_completion" &&
          modelAssignment.completion && (
            <SoftCompletionView
              completion={modelAssignment.completion as SoftCompletionData}
              accounts={accounts}
              householdModel={householdModel}
              resolveAccountName={resolveAccountName}
            />
          )}
      </Card.Body>
    </Card>
  );
}
